import { AUDIENCE_CODES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, ChoiceChips, CodeBlock, CopyButton, Field, Icon, Input, Select } from "@sagot/ui";
import { useController, useFieldArray, useWatch, type Control, type UseFormRegister } from "react-hook-form";
import type { AudienceOut } from "../shared/types";
import { ALGORITHMS, type ProviderValues } from "./provider-model";

const MAX_ROWS = 20;
/** Node.js signing sample for the portal (code, not copy — never translated). */
const SIGN_EXAMPLE = `jwt.sign({ sub: "user-123", role: "staff", name: "Ana Cruz" },
  privateKey, { algorithm: "RS256", expiresIn: "15m",
  issuer: "https://portal.example.com", audience: "sagot" })`;

interface FieldsProps {
  control: Control<ProviderValues>;
  register: UseFormRegister<ProviderValues>;
  audiences: readonly AudienceOut[];
}

function AudienceOptions({ audiences }: { audiences: readonly AudienceOut[] }) {
  const { t } = useTranslation();
  return (
    <>
      <option value="">{t("deployKnowledge.audiences.idp.pickAudience")}</option>
      {audiences.map((a) => (
        <option key={a.code} value={a.code}>{a.label}</option>
      ))}
    </>
  );
}

/** Audiences a verified identity unlocks (public is always included server-side). */
export function DefaultAudiences({ control, audiences }: Omit<FieldsProps, "register">) {
  const { t } = useTranslation();
  const { field } = useController({ control, name: "audiences" });
  const options = audiences.filter((a) => a.code !== AUDIENCE_CODES.public).map((a) => ({ value: String(a.code), label: a.label }));
  return (
    <fieldset className="space-y-1.5">
      <legend className="label">{t("deployKnowledge.audiences.idp.grants")}</legend>
      <ChoiceChips multiple aria-label={t("deployKnowledge.audiences.idp.grants")} options={options} value={field.value} onValueChange={field.onChange} />
      <p className="hint">{t("deployKnowledge.audiences.idp.grantsHint")}</p>
    </fieldset>
  );
}

export function JwtFields({ control, register, audiences }: FieldsProps) {
  const { t } = useTranslation();
  const rows = useFieldArray({ control, name: "claims" });
  const algorithm = useWatch({ control, name: "algorithm" });
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={t("deployKnowledge.audiences.idp.issuer")}>
          <Input className="font-mono text-xs" placeholder="https://portal.example.com" {...register("issuer")} />
        </Field>
        <Field label={t("deployKnowledge.audiences.idp.audience")}>
          <Input className="font-mono text-xs" {...register("audience")} />
        </Field>
        <Field label={t("deployKnowledge.audiences.idp.algorithm")}>
          <Select {...register("algorithm")}>
            {ALGORITHMS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </Select>
        </Field>
        {algorithm === "HS256" ? (
          <Field label={t("deployKnowledge.audiences.idp.sharedSecret")} hint={t("deployKnowledge.audiences.idp.secretHint")}>
            <Input type="password" autoComplete="new-password" className="font-mono text-xs" {...register("sharedSecret")} />
          </Field>
        ) : (
          <Field label={t("deployKnowledge.audiences.idp.jwksUrl")}>
            <Input className="font-mono text-xs" placeholder="https://portal.example.com/.well-known/jwks.json" {...register("jwksUrl")} />
          </Field>
        )}
      </div>
      <fieldset className="space-y-2">
        <legend className="label">{t("deployKnowledge.audiences.idp.claimMapping")}</legend>
        {rows.fields.map((row, index) => (
          <div key={row.id} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
            <Input className="h-9 font-mono text-xs" aria-label={t("deployKnowledge.audiences.idp.claim", { n: index + 1 })} placeholder="role" {...register(`claims.${index}.claim`)} />
            <Input className="h-9 text-xs" aria-label={t("deployKnowledge.audiences.idp.claimValue", { n: index + 1 })} placeholder="staff" {...register(`claims.${index}.value`)} />
            <Select className="h-9 text-xs" aria-label={t("deployKnowledge.audiences.idp.mapsTo", { n: index + 1 })} {...register(`claims.${index}.code`)}>
              <AudienceOptions audiences={audiences} />
            </Select>
            <Button variant="ghost" size="sm" iconOnly aria-label={t("deployKnowledge.audiences.idp.removeRow", { n: index + 1 })} onClick={() => { rows.remove(index); }}>
              <Icon name="x" />
            </Button>
          </div>
        ))}
        <Button variant="secondary" size="sm" disabled={rows.fields.length >= MAX_ROWS} onClick={() => { rows.append({ claim: "", value: "", code: "" }); }}>
          <Icon name="plus" />
          {t("deployKnowledge.audiences.idp.addClaim")}
        </Button>
      </fieldset>
      <CodeBlock label={t("deployKnowledge.audiences.idp.signExample")} code={SIGN_EXAMPLE} copyLabel={t("common.copy")} copiedLabel={t("common.copied")} />
    </>
  );
}

export function GroupFields({ control, register, audiences }: FieldsProps) {
  const { t } = useTranslation();
  const rows = useFieldArray({ control, name: "groups" });
  return (
    <fieldset className="space-y-2">
      <legend className="label">{t("deployKnowledge.audiences.idp.groupMapping")}</legend>
      {rows.fields.map((row, index) => (
        <div key={row.id} className="grid grid-cols-[1fr_1fr_auto] gap-2">
          <Input className="h-9 font-mono text-xs" aria-label={t("deployKnowledge.audiences.idp.group", { n: index + 1 })} {...register(`groups.${index}.group`)} />
          <Select className="h-9 text-xs" aria-label={t("deployKnowledge.audiences.idp.mapsTo", { n: index + 1 })} {...register(`groups.${index}.code`)}>
            <AudienceOptions audiences={audiences} />
          </Select>
          <Button variant="ghost" size="sm" iconOnly aria-label={t("deployKnowledge.audiences.idp.removeRow", { n: index + 1 })} onClick={() => { rows.remove(index); }}>
            <Icon name="x" />
          </Button>
        </div>
      ))}
      <Button variant="secondary" size="sm" disabled={rows.fields.length >= MAX_ROWS} onClick={() => { rows.append({ group: "", code: "" }); }}>
        <Icon name="plus" />
        {t("deployKnowledge.audiences.idp.addGroup")}
      </Button>
    </fieldset>
  );
}

export function EntraFields({ register, redirectUri }: Pick<FieldsProps, "register"> & { redirectUri: string | null }) {
  const { t } = useTranslation();
  return (
    <>
      <Field label={t("deployKnowledge.audiences.idp.tenantId")}>
        <Input className="font-mono text-xs" placeholder="00000000-0000-0000-0000-000000000000" {...register("entraTenantId")} />
      </Field>
      <Field label={t("deployKnowledge.audiences.idp.clientId")}>
        <Input className="font-mono text-xs" {...register("clientId")} />
      </Field>
      <Field label={t("deployKnowledge.audiences.idp.clientSecret")} hint={t("deployKnowledge.audiences.idp.secretHint")}>
        <Input type="password" autoComplete="new-password" className="font-mono text-xs" {...register("clientSecret")} />
      </Field>
      <div>
        <p className="label">{t("deployKnowledge.audiences.idp.redirectUri")}</p>
        {redirectUri ? (
          <div className="flex gap-2">
            <Input readOnly className="font-mono text-xs" value={redirectUri} aria-label={t("deployKnowledge.audiences.idp.redirectUri")} />
            <CopyButton value={redirectUri} label={t("common.copy")} copiedLabel={t("common.copied")} />
          </div>
        ) : (
          <p className="hint">{t("deployKnowledge.audiences.idp.redirectAfterSave")}</p>
        )}
        {redirectUri ? <p className="hint">{t("deployKnowledge.audiences.idp.redirectHint")}</p> : null}
      </div>
    </>
  );
}
